<template>
  <section>
    <header
      id="sizing-form"
      ref="heading"
      class="relative shadow-8 pt-2 noprint"
    >
      <div class="container">
        <div class="sticky-header">
          <!-- splits top bar into left and right sides -->
          <div class="left">
            <div class="sizing-bar">
              <div v-tooltip.bottom="sizingForm.data.value?.title" class="cell">
                <icon :icon="['fas', 'info-circle']" class="text-gray-600" />
                <span>{{ sizingForm.data.value?.title }}</span>
              </div>

              <div
                v-tooltip.bottom="sizingForm.data.value.well?.well_name"
                class="cell"
              >
                <icon :icon="['fas', 'oil-well']" class="text-gray-600" />
                <span>{{ sizingForm.data.value.well?.well_name }}</span>
              </div>

              <div
                v-tooltip.bottom="sizingForm.data.value.customer?.customer_name"
                class="cell"
              >
                <icon :icon="['fas', 'user']" class="text-gray-600" />
                <span>{{ sizingForm.data.value.customer?.customer_name }}</span>
              </div>

              <div class="cell">
                <icon :icon="['fas', 'ruler']" class="text-gray-600" />
                <span>{{
                  getEnumKeyByValue(
                    Display_Units_Enum,
                    sizingForm.data.value?.default_display_units
                  )
                }}</span>
              </div>

              <div class="cell">
                <icon
                  :icon="dispositionIcon"
                  class="text-gray-600"
                  :class="{
                    'fa-flip-horizontal':
                      sizingForm.data.value?.disposition ===
                      Sizing_Dispositions_Enum.Unknown
                  }"
                />
                <span>
                  {{
                    getEnumKeyByValue(
                      Sizing_Dispositions_Enum,
                      sizingForm.data.value?.disposition
                    )
                  }}
                </span>
              </div>

              <!-- where the notes show up, under the main sizing bar -->
              <transition name="vertical-slide">
                <div v-if="showNotes" class="cell full">
                  <span class="text-700">{{
                    sizingForm.data.value?.notes
                  }}</span>
                </div>
              </transition>
            </div>

            <div v-if="!isNewSizing" class="flex justify-content-between my-1">
              <div class="metadata">
                <div class="flex text-gray-900 font-light text-xs">
                  <p v-if="sizingForm.data.value.author">
                    Created By
                    {{
                      sizingForm.data.value.author.display_name ||
                      sizingForm.data.value.author.email
                    }}
                  </p>
                  <p v-else class="font-italic ml-2 mr-4">Imported</p>
                  <p
                    v-if="sizingForm.data.value.journal_updated_at"
                    class="ml-4"
                  >
                    Saved
                    {{
                      format(
                        new Date(sizingForm.data.value.journal_updated_at),
                        "MMMM-dd-yyyy HH:mm:ss"
                      )
                    }}
                  </p>
                  <p
                    v-if="isImportedFromLegacy"
                    class="ml-4 font-bold text-danger-500"
                  >
                    You cannot edit this sizing since it was imported from
                    legacy reaver. Please clone the sizing so it is recreated
                    correctly in the new system.
                  </p>
                  <p
                    v-else-if="isSizingReadonly"
                    class="ml-4 font-bold text-danger-500"
                  >
                    You cannot edit this sizing since you are not the owner.
                    Please clone it by exporting and re-importing it so you own
                    a copy.
                  </p>
                </div>
                <router-link
                  v-if="
                    sizingForm.data.value?.parent_id &&
                    !sizingForm.data.value?.parent_sizing?.journal_deleted_at
                  "
                  :to="{
                    name: 'WellInfo',
                    params: getRouteParamsFromSizing(
                      sizingForm.data.value?.parent_sizing,
                      true
                    )
                  }"
                  class="p-button-text pl-4"
                  label="Parent Sizing"
                >
                  <i class="far fa-link"></i>
                  Parent Sizing
                </router-link>
                <div
                  v-else-if="
                    sizingForm.data.value?.parent_id &&
                    sizingForm.data.value?.parent_sizing?.journal_deleted_at
                  "
                  class="flex text-gray-900 font-light text-xs pl-4"
                >
                  Cloned from: &nbsp;
                  <i>{{ sizingForm.data.value?.parent_sizing?.title }}</i>
                  &nbsp;(deleted)
                </div>
                <div class="text-gray-900 text-xs">
                  <Button
                    v-if="showNotes"
                    class="p-button-sm p-button-link"
                    @click="showNotes = false"
                  >
                    Hide Notes
                  </Button>
                  <Button
                    v-else
                    :disabled="!sizingForm.data.value?.notes"
                    :label="sizingForm.data.value?.notes ? 'Show Notes' : ''"
                    class="p-button-sm p-button-link"
                    @click="showNotes = true"
                  />
                </div>
              </div>
              <router-link
                v-if="routeName === 'Reports'"
                custom
                :to="{
                  name: 'WellInfo',
                  params: {
                    sizingId: route.params.sizingId,
                    scenarioId: getInstalledOrLatestScenarioId(
                      props.sizing,
                      props.sizing?.scenarios
                    )
                  }
                }"
              >
                <template #default="{ navigate }">
                  <Button
                    class="w-12rem ml-2"
                    icon="far fa-arrow-from-right"
                    label="Back to Scenarios"
                    @click="navigate"
                  />
                </template>
              </router-link>
            </div>
          </div>
          <SizingMenu
            class="mx-2"
            :sizing-id="String(route.params.sizingId)"
            :scenario-id="scenarioId"
            :perfeval-id="perfevalId"
            :route-name="routeName"
            @delete="isDeleteSizingDialogOpen = true"
            @export-sizing="handleStartExport"
            @export-surveys="handleStartSurveyExport"
            @clone="handleStartClone"
          />
        </div>
      </div>
    </header>
    <router-view v-if="sizingForm.data.value.id" v-slot="{ Component }">
      <keep-alive :include="['ScenarioDetail']" :exclude="['ChartSwitcher']">
        <component
          :is="Component"
          :key="route.params.scenarioId"
          ref="routerViewComponent"
        />
      </keep-alive>
    </router-view>
    <DiscardChangesDialog
      v-model:visible="isDiscardChangesDialogOpen"
      :scenarioTitle="selectedScenarioTitle"
      @confirmed="handleConfirmDialog"
    />
    <CloneDialog
      v-model:shouldClone="isCloneDialogOpen"
      type="Sizing"
      @save-clone="handleCloneSizing"
    />
    <a ref="exportDownloadAnchor" style="display: none"></a>
    <DeleteSizingDialog
      v-model:should-delete-sizing="isDeleteSizingDialogOpen"
    />
    <ExportSurveyDialog
      v-if="isExportSurveyDialogOpen && sizing"
      v-model:shouldExportSurvey="isExportSurveyDialogOpen"
      :sizings="[sizing]"
    />
    <ExportSizingDialog
      v-if="isExportSizingDialogOpen && sizing"
      v-model:should-export-sizing="isExportSizingDialogOpen"
      :sizings="[sizing]"
    />
  </section>
</template>

<script setup lang="ts">
import type ScenarioDetail from "@/components/scenarios/ScenarioDetail.vue";

import { computed, onMounted, provide, reactive, ref } from "vue";
import {
  Sizing_Dispositions_Enum,
  Display_Units_Enum,
  Sizing_Pumping_Type_Enum
} from "gql/api";
import type {} from "vue-router";
import { useRouter, useRoute } from "vue-router";
import { useIntersectionObserver } from "@vueuse/core";
import type { Customers } from "gql/api";
import { DiscardAction, getEnumKeyByValue } from "@/utils/enums";
import { format } from "date-fns";
import useForm from "@/composables/forms/useForm";
import {
  sizingKey,
  sizingFormKey,
  scenariosKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { required } from "@vuelidate/validators";
import useEmptySizing from "@/composables/useEmptySizing";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import SizingMenu from "@/components/sizing/SizingMenu.vue";
import exportSizing from "@/utils/exports/exportSizing";
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import useImportSizing from "@/composables/useImportSizing";
import { buildInfoKey, primaryCustomerKey } from "@/injectionKeys";
import DiscardChangesDialog from "@/components/DiscardChangesDialog.vue";
import { injectStrict } from "@/utils/utils";
import CloneDialog from "@/components/CloneDialog.vue";
import DeleteSizingDialog from "@/components/sizing/DeleteSizingDialog.vue";
import ExportSizingDialog from "@/components/sizing/ExportSizingDialog.vue";
import ExportSurveyDialog from "@/components/sizing/ExportSurveyDialog.vue";
import { provideConfirmationMethodsKey } from "@/plugins/confirmationPlugin";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import {
  getInstalledOrLatestScenarioId,
  getRouteParamsFromSizing
} from "@/utils/scenarioUtils";
import { LS_KEY } from "@/libs/calculations/constants";

const props = defineProps<{
  sizing: Sizing;
  sizingId?: string | null;
  scenarioId?: string | null;
  perfevalId?: string | null;
  userCustomers: Array<Customers>;
  isFetchingSizing: boolean;
}>();

const buildInfo = injectStrict(buildInfoKey);
const router = useRouter();
const route = useRoute();
const routeName = computed(() => router.currentRoute.value.name);

const routerViewComponent = ref<InstanceType<typeof ScenarioDetail>>();
const exportDownloadAnchor = ref<HTMLAnchorElement>();

const heading = ref(null);
const sizingFormVisible = ref(true);
const showNotes = ref(false);

const userCustomers = computed(() => props.userCustomers);
const primaryCustomer = computed(() => userCustomers.value.at(0));

const isNewSizing = computed(() => route.params.sizingId === "new");

const scenarios = computed(() => props.sizing?.scenarios || []);
const sizing = computed(() => props.sizing);
const emptySizing = useEmptySizing(primaryCustomer);

const sizingForm = useForm(sizing, {
  title: { required },
  disposition: { required },
  default_display_units: { required }
});

provide(primaryCustomerKey, primaryCustomer);
provide(scenariosKey, scenarios);
provide(sizingFormKey, sizingForm);
provide(sizingKey, sizing);

const dispositionIcon = computed(() => {
  if (sizingForm.data.value.disposition === Sizing_Dispositions_Enum.Accepted) {
    return ["far", "check-circle"];
  } else if (
    sizingForm.data.value.disposition === Sizing_Dispositions_Enum.Closed
  ) {
    return ["far", "times-circle"];
  } else if (
    sizingForm.data.value.disposition === Sizing_Dispositions_Enum.Rejected
  ) {
    return ["far", "ban"];
  } else if (
    sizingForm.data.value.disposition === Sizing_Dispositions_Enum.Unknown
  ) {
    return ["far", "undo"];
  } else {
    return ["far", "undo"];
  }
});

useIntersectionObserver(heading, ([{ isIntersecting }]) => {
  sizingFormVisible.value = isIntersecting;
});

const { isSizingReadonly, isImportedFromLegacy } = useEditPermissions(
  sizingForm,
  null
);

const ddvTickFrequencies = reactive({ x: "4em", y: "2.25em" });
provide("ddvTickFrequencies", ddvTickFrequencies);

onMounted(() => {
  if (!isNewSizing.value) return; // only for brand-new sizings

  const default_display_units = sizingForm.data.value.default_display_units;

  const pumpingType =
    (localStorage.getItem(LS_KEY) as
      | Sizing_Pumping_Type_Enum.Hps
      | Sizing_Pumping_Type_Enum.EspOnShore
      | null) ?? Sizing_Pumping_Type_Enum.EspOnShore;

  if (pumpingType === Sizing_Pumping_Type_Enum.Hps) {
    sizingForm.update({
      ...emptySizing.value,
      pumping_type: pumpingType,
      default_display_units:
        pumpingType === Sizing_Pumping_Type_Enum.Hps
          ? Display_Units_Enum.UsHps
          : default_display_units
    });
  } else {
    sizingForm.update({
      ...emptySizing.value,
      pumping_type: pumpingType
    });
  }
});

provide(sizingFormKey, sizingForm);

const selectedScenarioTitle = computed(() => {
  const scenario = scenarios.value?.find(
    (scenario: Scenario) => scenario.id === selectedScenarioId.value
  );
  return scenario?.title || "";
});
const selectedScenarioId = computed(() => {
  // if sizing is downloaded but scenario id from route
  // is not in the sizing, throw out scenario id param
  const scenarioIds = sizing.value?.scenarios?.map(
    (scenario: Scenario) => scenario.id
  );
  if (
    route.params.scenarioId !== "new" &&
    sizing.value?.id &&
    scenarioIds &&
    !scenarioIds.includes(route.params.scenarioId)
  ) {
    return null;
  }

  if (!route.params?.scenarioId && !props.isFetchingSizing) {
    const installed = sizing.value?.installed_scenario_id;
    const first = [...(scenarios.value || [])].pop()?.id;
    // we're not fetching, use the installed id, if not present, use the first one,
    // if that's not there, then send them to 'new'
    return installed || first || "new";
  }
  return route.params?.scenarioId; // including 'new'
});

const isDiscardChangesDialogOpen = ref(false);

const handleConfirmDialog = async (discarded: DiscardAction) => {
  isDiscardChangesDialogOpen.value = false;
  if (discarded !== DiscardAction.CANCEL) {
    if (discarded === DiscardAction.SAVE) {
      if (typeof routerViewComponent.value?.onSave === "function") {
        await routerViewComponent.value?.onSave();
      }
    } else {
      if (typeof routerViewComponent.value?.resetForms === "function") {
        routerViewComponent.value?.resetForms();
      }
    }
  }
  if (!togglePromise.value) return;
  togglePromise.value(discarded);
  togglePromise.value = null;
};

const togglePromise = ref<((confirmed: DiscardAction) => void) | null>(null);

const toggleDiscardChanges: () => Promise<boolean> = async () => {
  isDiscardChangesDialogOpen.value = true;
  return new Promise(resolve => {
    togglePromise.value = (discarded: DiscardAction) => {
      resolve(discarded !== DiscardAction.CANCEL);
    };
  });
};

const provideConfirmationMethods = injectStrict(provideConfirmationMethodsKey);

provideConfirmationMethods({
  isDirty: () => {
    if (typeof routerViewComponent.value?.isDirty === "function") {
      return routerViewComponent.value?.isDirty() || false;
    }
    return false;
  },
  toggleDiscardChanges
});

const checkIfCanContinue = async () => {
  if (
    typeof routerViewComponent.value?.isDirty === "function" &&
    routerViewComponent.value?.isDirty() &&
    typeof routerViewComponent.value?.isNewScenario === "function" &&
    !routerViewComponent.value.isNewScenario()
  ) {
    return toggleDiscardChanges();
  }
  return true;
};

const handleStartExport = async () => {
  isExportSizingDialogOpen.value = await checkIfCanContinue();
};
const handleStartSurveyExport = async () => {
  isExportSurveyDialogOpen.value = await checkIfCanContinue();
};
const handleStartClone = async () => {
  isCloneDialogOpen.value = await checkIfCanContinue();
};

const isCloneDialogOpen = ref(false);
const isDeleteSizingDialogOpen = ref(false);
const isExportSizingDialogOpen = ref(false);
const isExportSurveyDialogOpen = ref(false);

const importSizing = useImportSizing(true);

async function handleCloneSizing(title: string) {
  if (sizing.value) {
    const { snapshot } = exportSizing(buildInfo, sizing.value);
    snapshot.payload.title = title;
    const importedSizing = await importSizing(snapshot);

    await haltUntilCompletion();
    isCloneDialogOpen.value = false;
    const scenarioId =
      getInstalledOrLatestScenarioId(
        importedSizing,
        importedSizing.scenarios
      ) || "new";
    const name = scenarioId === "new" ? "ScenarioInfo" : "WellInfo";
    await router.push({
      name,
      params: {
        sizingId: importedSizing?.id,
        scenarioId
      }
    });
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/theme-spyglass/components/typography.scss";

header {
  position: relative;
  z-index: 3;
  background: var(--surface-200);
}
.sticky-header {
  position: sticky;
  top: -40px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;

  .left {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 0;
    min-width: 75%;
    .sizing-bar {
      display: grid;
      align-items: baseline;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      grid-template-rows: repeat(minmax(3em, 1fr));
      padding: 0.5em;
      gap: 0.5em;
      background: var(--surface-0);
      box-shadow: var(--shadow-main);
      border: 1px solid var(--border-main);
      border-radius: var(--border-radius);
      flex: 1 1 auto;

      .cell {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        &.full {
          grid-column: 1/-1;
          & > span {
            white-space: normal;
            overflow: visible;
            text-overflow: unset;
          }
        }

        svg {
          margin-right: 0.25em;
        }
        span {
          @extend .heading-3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
          max-width: 100%;
        }
      }
    }
    .metadata {
      margin: 0 0.5em;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
  .right {
    flex: 0 1 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
